import React, { useMemo, useState } from 'react';
import { FaBoxes, FaCogs, FaHome, FaFileAlt, FaReceipt, FaFileContract } from 'react-icons/fa';
import Tab, { TabProps } from '../../../../molecules/Tab';
import InventoryItemForm from './InventoryItemForm';
import MachineryItemForm from './MachineryItemForm';
import RealEstateItemForm from './RealEstateItemForm';
import SecurityItemForm from './SecurityItemForm';
import {
    Collaterals,
    CollateralAssets,
    InventoryItem,
    MachineryItem,
    RealEstateItem,
    SecurityItem,
    CollateralsWithReceivables,
    CollateralAssetsWithReceivables,
} from '../../types/operations';

interface CollateralsTabManagerProps {
    collaterals: Collaterals | CollateralsWithReceivables;
    onChange: (collaterals: Collaterals | CollateralsWithReceivables) => void;
    disabled?: boolean;
    /** Render prop for the Receivables tab — pass your <ReceivablesManager ... /> here */
    receivablesSlot?: React.ReactNode;
    /** Render prop for the Contracts tab — pass your <ContractGuaranteeManager ... /> here */
    contractsSlot?: React.ReactNode;
}

const recalculateTotals = (assets: CollateralAssets | CollateralAssetsWithReceivables): { total_value: number; total_quantity: number } => {
    const extAssets = assets as CollateralAssetsWithReceivables;
    const rv = extAssets.receivables?.total || 0;
    const ct = extAssets.contracts?.total || 0;
    const inv = assets.inventory?.total || 0;
    const mch = assets.machinery?.total || 0;
    const re = assets.real_estate?.total || 0;
    const sec = assets.securities?.total || 0;
    const rq = extAssets.receivables?.quantity || 0;
    const cq = extAssets.contracts?.quantity || 0;
    const iq = assets.inventory?.quantity || 0;
    const mq = assets.machinery?.quantity || 0;
    const req = assets.real_estate?.quantity || 0;
    const sq = assets.securities?.quantity || 0;
    return { total_value: rv + ct + inv + mch + re + sec, total_quantity: rq + cq + iq + mq + req + sq };
};

const formatCurrency = (value: number): string =>
    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const CollateralsTabManager: React.FC<CollateralsTabManagerProps> = ({
    collaterals,
    onChange,
    disabled = false,
    receivablesSlot,
    contractsSlot,
}) => {
    const [tabIndex, setTabIndex] = useState(0);

    // Show receivables/contracts tabs only when slots are provided
    const showReceivablesTab = receivablesSlot !== undefined;
    const showContractsTab = contractsSlot !== undefined;

    const tabItems: TabProps[] = useMemo(() => {
        const items: TabProps[] = [];
        let idx = 0;
        const extAssets = collaterals.assets as CollateralAssetsWithReceivables;

        if (showReceivablesTab) {
            const recQty = extAssets.receivables?.quantity || 0;
            items.push({
                id: 'receivables',
                title: `Recebíveis (${recQty})`,
                icon: FaReceipt,
                index: idx++,
            });
        }

        if (showContractsTab) {
            const ctQty = extAssets.contracts?.quantity || 0;
            items.push({
                id: 'contracts',
                title: `Contratos (${ctQty})`,
                icon: FaFileContract,
                index: idx++,
            });
        }

        const invQty = collaterals.assets.inventory?.quantity || 0;
        items.push({
            id: 'inventory',
            title: `Estoque (${invQty})`,
            icon: FaBoxes,
            index: idx++,
        });

        const mchQty = collaterals.assets.machinery?.quantity || 0;
        items.push({
            id: 'machinery',
            title: `Maquinário (${mchQty})`,
            icon: FaCogs,
            index: idx++,
        });

        const reQty = collaterals.assets.real_estate?.quantity || 0;
        items.push({
            id: 'real_estate',
            title: `Imóveis (${reQty})`,
            icon: FaHome,
            index: idx++,
        });

        const secQty = collaterals.assets.securities?.quantity || 0;
        items.push({
            id: 'securities',
            title: `Títulos (${secQty})`,
            icon: FaFileAlt,
            index: idx++,
        });

        return items;
    }, [collaterals.assets, showReceivablesTab, showContractsTab]);

    const activeTabId = tabItems[tabIndex]?.id || 'inventory';

    const updateAssets = (updatedAssets: CollateralAssets | CollateralAssetsWithReceivables) => {
        const { total_value, total_quantity } = recalculateTotals(updatedAssets);
        onChange({ total_value, total_quantity, assets: updatedAssets } as Collaterals | CollateralsWithReceivables);
    };

    const handleAddInventory = (item: InventoryItem) => {
        const currentItems = collaterals.assets.inventory?.items || [];
        const newItems = [...currentItems, item];
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            inventory: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleRemoveInventory = (index: number) => {
        const currentItems = collaterals.assets.inventory?.items || [];
        const newItems = currentItems.filter((_, i) => i !== index);
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            inventory: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleAddMachinery = (item: MachineryItem) => {
        const currentItems = collaterals.assets.machinery?.items || [];
        const newItems = [...currentItems, item];
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            machinery: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleRemoveMachinery = (index: number) => {
        const currentItems = collaterals.assets.machinery?.items || [];
        const newItems = currentItems.filter((_, i) => i !== index);
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            machinery: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleAddRealEstate = (item: RealEstateItem) => {
        const currentItems = collaterals.assets.real_estate?.items || [];
        const newItems = [...currentItems, item];
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            real_estate: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleRemoveRealEstate = (index: number) => {
        const currentItems = collaterals.assets.real_estate?.items || [];
        const newItems = currentItems.filter((_, i) => i !== index);
        const total = newItems.reduce((sum, i) => sum + (i.value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            real_estate: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleAddSecurity = (item: SecurityItem) => {
        const currentItems = collaterals.assets.securities?.items || [];
        const newItems = [...currentItems, item];
        const total = newItems.reduce((sum, i) => sum + (i.initial_value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            securities: { items: newItems, total, quantity: newItems.length },
        });
    };

    const handleRemoveSecurity = (index: number) => {
        const currentItems = collaterals.assets.securities?.items || [];
        const newItems = currentItems.filter((_, i) => i !== index);
        const total = newItems.reduce((sum, i) => sum + (i.initial_value || 0), 0);
        updateAssets({
            ...collaterals.assets,
            securities: { items: newItems, total, quantity: newItems.length },
        });
    };

    const renderTabContent = () => {
        switch (activeTabId) {
            case 'receivables':
                return receivablesSlot || null;
            case 'contracts':
                return contractsSlot || null;
            case 'inventory':
                return (
                    <InventoryItemForm
                        items={collaterals.assets.inventory?.items || []}
                        onAdd={handleAddInventory}
                        onRemove={handleRemoveInventory}
                        disabled={disabled}
                    />
                );
            case 'machinery':
                return (
                    <MachineryItemForm
                        items={collaterals.assets.machinery?.items || []}
                        onAdd={handleAddMachinery}
                        onRemove={handleRemoveMachinery}
                        disabled={disabled}
                    />
                );
            case 'real_estate':
                return (
                    <RealEstateItemForm
                        items={collaterals.assets.real_estate?.items || []}
                        onAdd={handleAddRealEstate}
                        onRemove={handleRemoveRealEstate}
                        disabled={disabled}
                    />
                );
            case 'securities':
                return (
                    <SecurityItemForm
                        items={collaterals.assets.securities?.items || []}
                        onAdd={handleAddSecurity}
                        onRemove={handleRemoveSecurity}
                        disabled={disabled}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4 mt-6">
            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Garantias</h3>

            {/* Summary bar */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-slate-800">
                <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        Total de Garantias
                    </span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(collaterals.total_value)}
                    </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {collaterals.total_quantity} {collaterals.total_quantity === 1 ? 'item' : 'itens'}
                </span>
            </div>

            {/* Tabs */}
            <Tab tabItems={tabItems} tabIndex={tabIndex} setTabIndex={setTabIndex} />

            {/* Tab content */}
            <div className="mt-4">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default CollateralsTabManager;
